import React, { useRef } from "react";
import { 
  X, 
  Printer, 
  Download, 
  FileText, 
  Calendar, 
  Building2, 
  User, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  FileCheck
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";

const ContractPreviewModal = ({ isOpen, onClose, contract }) => {
  const printRef = useRef();

  if (!contract) return null;

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Hoàn thành": return "text-green-600 bg-green-50 border-green-200";
      case "Đang thực hiện": return "text-blue-600 bg-blue-50 border-blue-200";
      case "Chậm tiến độ": return "text-red-600 bg-red-50 border-red-200";
      default: return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Hoàn thành": return <CheckCircle2 className="w-4 h-4" />;
      case "Đang thực hiện": return <Clock className="w-4 h-4" />;
      case "Chậm tiến độ": return <AlertTriangle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[110] no-print"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-[111] no-print"
          >
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[95vh]">
              {/* Header */}
              <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-white sticky top-0 z-10 no-print">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-red-700/5 rounded-2xl text-red-700 shadow-sm border border-red-700/10">
                    <FileCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight leading-tight">
                      Xem trước <span className="text-red-700">Hợp đồng</span>
                    </h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-1">
                      {contract.contract_code} — {contract.title}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePrint}
                    className="p-3 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl transition-all flex items-center gap-2 font-bold text-xs uppercase"
                  >
                    <Printer className="w-4 h-4" /> In ấn
                  </button>
                  {contract.file_unsigned && (
                    <a
                      href={contract.file_unsigned}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-red-700 hover:bg-red-800 text-white rounded-xl transition-all flex items-center gap-2 font-bold text-xs uppercase shadow-lg shadow-red-700/20"
                    >
                      <Download className="w-4 h-4" /> Tải về
                    </a>
                  )}
                  <button
                    onClick={onClose}
                    className="p-3 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-gray-50/50">
                {/* Print Content Area */}
                <div 
                  ref={printRef}
                  className="bg-white shadow-sm border border-gray-100 rounded-[2rem] p-12 mx-auto max-w-[210mm] print:shadow-none print:border-none print:p-0 print:m-0"
                >
                  {/* Internal Print Styles */}
                  <style>{`
                    @media print {
                      .no-print { display: none !important; }
                      body { background: white !important; }
                      .print-only { display: block !important; }
                      @page { margin: 20mm; }
                      .custom-scrollbar::-webkit-scrollbar { display: none; }
                    }
                  `}</style>

                  {/* Document Header */}
                  <div className="flex justify-between items-start border-b-2 border-slate-900 pb-8 mb-12">
                    <div className="space-y-2">
                       <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Hợp Đồng Dịch Vụ</h1>
                       <div className="flex items-center gap-2 text-red-700 font-black text-sm uppercase tracking-widest">
                         <span>Mã: {contract.contract_code}</span>
                       </div>
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-slate-900 font-black text-lg">IMA VIETNAM</p>
                      <p className="text-slate-400 text-xs font-bold uppercase tracking-widest leading-relaxed max-w-[200px]">
                        Hệ thống Giáo dục & Công nghệ Hamesoft
                      </p>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-x-12 gap-y-8 mb-12">
                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-[0.2em]">Khách hàng đối tác</p>
                      <p className="text-base font-black text-slate-900">{contract.customer_name || "N/A"}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-[0.2em]">Chi nhánh thực hiện</p>
                      <p className="text-base font-black text-slate-900">{contract.branch_name || "N/A"}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-[0.2em]">Thời gian hiệu lực</p>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-black text-slate-900">{contract.start_date ? format(new Date(contract.start_date), "dd/MM/yyyy") : "--"}</span>
                        <span className="text-gray-300">→</span>
                        <span className="text-sm font-black text-slate-900">{contract.end_date ? format(new Date(contract.end_date), "dd/MM/yyyy") : "--"}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-[0.2em]">Trạng thái hợp đồng</p>
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${getStatusColor(contract.status)}`}>
                        {getStatusIcon(contract.status)}
                        {contract.status || "Chưa xác định"}
                      </div>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="space-y-8 mb-12">
                    <div className="space-y-4">
                      <h3 className="text-xs font-black uppercase tracking-[0.3em] text-slate-900 border-l-4 border-red-700 pl-4 py-1 bg-red-50/50">
                        Nội dung chi tiết
                      </h3>
                      <div className="text-slate-800 text-sm leading-relaxed whitespace-pre-wrap font-medium font-serif italic border border-slate-100 p-8 rounded-2xl bg-slate-50/20 shadow-inner">
                        {contract.content || "Chưa có nội dung chi tiết..."}
                      </div>
                    </div>

                    {contract.note && (
                      <div className="space-y-4">
                        <h3 className="text-xs font-black uppercase tracking-[0.3em] text-slate-900 border-l-4 border-gray-400 pl-4 py-1 bg-gray-50/50">
                          Ghi chú bổ sung
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {contract.note}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Signature Section */}
                  <div className="grid grid-cols-2 gap-12 mt-24">
                    <div className="text-center space-y-16">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900">Đại diện Khách hàng</p>
                      <div className="h-px w-32 bg-gray-200 mx-auto" />
                    </div>
                    <div className="text-center space-y-16">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900">Đại diện IMA Vietnam</p>
                      <div className="h-px w-32 bg-gray-200 mx-auto" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-8 border-t border-gray-50 flex items-center justify-center bg-white no-print">
                <button
                  onClick={onClose}
                  className="px-12 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase text-[11px] tracking-widest transition-all shadow-xl hover:scale-105 active:scale-95 hover:bg-slate-800"
                >
                  Hoàn tất và Đóng
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContractPreviewModal;
